"use client";

import { useCallback, useMemo, useRef } from "react";
import type { RefObject } from "react";

import type { PolygonIdentifier, PolygonsLayerRef } from "@/providers/maps/types";
import {
  captureBaselineIfMissing,
  clearBaselines,
  getDirtyDraftFeatures,
  hasAnyDirtyDraftFeatures,
  isDraftFeatureDirty,
  markUserTouched,
  pruneOrphanBaselines,
  pruneUnchangedBoundaryIds,
  removeBaseline,
  replaceBaselineIfNotUserTouched,
  resetUserTouched,
  resolveFeaturesForDirtyCheck,
  syncCombineDraftBaselines,
  type BaselineCompareMode,
  type BoundaryDraftBaselineStore,
  type LiveEditingSnapshot,
  type UserTouchedStore,
} from "@/providers/maps/utils/boundary-draft-baseline.utils";
import { isValidPolygon } from "@/providers/maps/utils/is-valid-polygon";

export interface UseBoundaryDraftDirtyStateOptions {
  layerRef: RefObject<PolygonsLayerRef | null>;
  draftFeatures: GeoJSON.Feature<GeoJSON.Polygon>[];
  draftFeaturesRef: RefObject<GeoJSON.Feature<GeoJSON.Polygon>[]>;
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string;
  isNewDraftId: (id: string) => boolean;
  compareMode: BaselineCompareMode;
  isValidLiveGeometry?: (geometry: GeoJSON.Geometry | undefined) => boolean;
}

export interface PruneUnchangedDraftFeaturesArgs {
  removeFromDraftState: (idsToRemove: string[]) => void;
  onAfterPrune?: () => void;
  preserveBoundaryIds?: string[];
}

export interface PersistCurrentEditingOptions {
  preserveBoundaryIds?: string[];
  /** Keep the live editing boundary in draft even when unchanged (draw → selection). */
  keepLiveInDraft?: boolean;
}

export interface CreatePersistCurrentEditingDeps {
  buildFeatureFromLive: (
    live: LiveEditingSnapshot,
    existing: GeoJSON.Feature<GeoJSON.Polygon> | null,
  ) => GeoJSON.Feature<GeoJSON.Polygon> | null;
  upsertToDraft: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => void;
  removeFromDraft: (boundaryId: string) => void;
  getExistingFeature: (boundaryId: string) => GeoJSON.Feature<GeoJSON.Polygon> | null;
  pruneAfterPersist: (options?: PersistCurrentEditingOptions) => void;
  onMissingLayerId?: () => void;
}

function defaultIsValidLiveGeometry(geometry: GeoJSON.Geometry | undefined): boolean {
  if (geometry?.type !== "Polygon") {
    return false;
  }

  return isValidPolygon({
    type: "Feature",
    geometry,
    properties: {},
  });
}

export function useBoundaryDraftDirtyState(options: UseBoundaryDraftDirtyStateOptions) {
  const {
    layerRef,
    draftFeatures,
    draftFeaturesRef,
    getBoundaryId,
    isNewDraftId,
    compareMode,
    isValidLiveGeometry = defaultIsValidLiveGeometry,
  } = options;

  const draftBaselinesRef = useRef<BoundaryDraftBaselineStore>(new Map());
  const userTouchedRef = useRef<UserTouchedStore>(new Map());

  const selectionPinnedDraftIdsRef = useRef<Set<string>>(new Set());

  const pinBoundaryForSelection = useCallback((boundaryId: string) => {
    if (!boundaryId) {
      return;
    }

    selectionPinnedDraftIdsRef.current.add(boundaryId);
  }, []);

  const unpinBoundaryForSelection = useCallback((boundaryId: string) => {
    selectionPinnedDraftIdsRef.current.delete(boundaryId);
  }, []);

  const getLiveEditingSnapshot = useCallback((): LiveEditingSnapshot | null => {
    const editingLayerRef = layerRef.current;
    const editingId = editingLayerRef?.editingIdentifier?.value;
    const shape = editingLayerRef?.getCurrentShape();
    if (
      editingId == null ||
      editingId === "" ||
      shape == null ||
      !isValidLiveGeometry(shape.geometry)
    ) {
      return null;
    }

    return {
      boundaryId: String(editingId),
      geometry: shape.geometry as GeoJSON.Polygon,
      properties: shape.properties as Record<string, unknown> | undefined,
    };
  }, [isValidLiveGeometry, layerRef]);

  const getFeaturesForDirtyCheck = useCallback(
    () =>
      resolveFeaturesForDirtyCheck(
        draftFeaturesRef.current,
        getLiveEditingSnapshot(),
        getBoundaryId,
      ),
    [draftFeaturesRef, getBoundaryId, getLiveEditingSnapshot],
  );

  const hasDraftChanges = useMemo(
    () =>
      hasAnyDirtyDraftFeatures(
        resolveFeaturesForDirtyCheck(
          draftFeatures,
          getLiveEditingSnapshot(),
          getBoundaryId,
        ),
        draftBaselinesRef.current,
        getBoundaryId,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      ),
    [compareMode, draftFeatures, getBoundaryId, getLiveEditingSnapshot, isNewDraftId],
  );

  const getHasDraftChanges = useCallback(
    () =>
      hasAnyDirtyDraftFeatures(
        getFeaturesForDirtyCheck(),
        draftBaselinesRef.current,
        getBoundaryId,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      ),
    [compareMode, getBoundaryId, getFeaturesForDirtyCheck, isNewDraftId],
  );

  const getDirtyFeatures = useCallback(
    () =>
      getDirtyDraftFeatures(
        draftFeaturesRef.current,
        draftBaselinesRef.current,
        getBoundaryId,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      ),
    [compareMode, draftFeaturesRef, getBoundaryId, isNewDraftId],
  );

  const getDirtyFeaturesIncludingLiveEditing = useCallback(
    () =>
      getDirtyDraftFeatures(
        getFeaturesForDirtyCheck(),
        draftBaselinesRef.current,
        getBoundaryId,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      ),
    [compareMode, getBoundaryId, getFeaturesForDirtyCheck, isNewDraftId],
  );

  const captureBaseline = useCallback(
    (feature: GeoJSON.Feature<GeoJSON.Polygon>) => {
      const id = getBoundaryId(feature);
      if (!id) {
        return;
      }

      captureBaselineIfMissing(
        draftBaselinesRef.current,
        id,
        feature,
        isNewDraftId,
        compareMode,
      );
    },
    [compareMode, getBoundaryId, isNewDraftId],
  );

  const replaceBaselineFromFeature = useCallback(
    (feature: GeoJSON.Feature<GeoJSON.Polygon>) => {
      const id = getBoundaryId(feature);
      if (!id) {
        return;
      }

      replaceBaselineIfNotUserTouched(
        draftBaselinesRef.current,
        id,
        feature,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      );
    },
    [compareMode, getBoundaryId, isNewDraftId],
  );

  const clearAllDirtyState = useCallback(() => {
    clearBaselines(draftBaselinesRef.current, userTouchedRef.current);
    selectionPinnedDraftIdsRef.current.clear();
  }, []);

  const removeDirtyStateForId = useCallback((boundaryId: string) => {
    removeBaseline(draftBaselinesRef.current, boundaryId, userTouchedRef.current);
    unpinBoundaryForSelection(boundaryId);
  }, [unpinBoundaryForSelection]);

  const markBoundaryUserTouched = useCallback((boundaryId: string | number) => {
    markUserTouched(userTouchedRef.current, String(boundaryId));
  }, []);

  const syncCombineDirtyState = useCallback(
    (idsToDelete: string[], mergedId?: string) => {
      syncCombineDraftBaselines(
        draftBaselinesRef.current,
        idsToDelete,
        isNewDraftId,
        userTouchedRef.current,
      );
      for (const id of idsToDelete) {
        unpinBoundaryForSelection(String(id));
      }
      if (mergedId) {
        markUserTouched(userTouchedRef.current, mergedId);
      }
    },
    [isNewDraftId, unpinBoundaryForSelection],
  );

  const syncSelectionPinsToBoundaryIds = useCallback((boundaryIds: string[]) => {
    selectionPinnedDraftIdsRef.current = new Set(
      boundaryIds.filter((boundaryId) => Boolean(boundaryId)),
    );
  }, []);

  const pruneOrphanDirtyState = useCallback(
    (features: GeoJSON.Feature<GeoJSON.Polygon>[]) => {
      pruneOrphanBaselines(
        draftBaselinesRef.current,
        features,
        getBoundaryId,
        userTouchedRef.current,
      );
      const activeIds = new Set(features.map(getBoundaryId).filter(Boolean));
      for (const pinnedId of [...selectionPinnedDraftIdsRef.current]) {
        if (!activeIds.has(pinnedId)) {
          selectionPinnedDraftIdsRef.current.delete(pinnedId);
        }
      }
    },
    [getBoundaryId],
  );

  const pruneUnchangedDraftFeatures = useCallback(
    (args: PruneUnchangedDraftFeaturesArgs) => {
      const preserve = new Set([
        ...(args.preserveBoundaryIds ?? []),
        ...selectionPinnedDraftIdsRef.current,
      ]);
      const idsToRemove = pruneUnchangedBoundaryIds(
        getFeaturesForDirtyCheck(),
        draftBaselinesRef.current,
        getBoundaryId,
        isNewDraftId,
        compareMode,
        userTouchedRef.current,
      ).filter((boundaryId) => !preserve.has(boundaryId));
      if (idsToRemove.length === 0) {
        return;
      }

      for (const boundaryId of idsToRemove) {
        removeBaseline(draftBaselinesRef.current, boundaryId, userTouchedRef.current);
      }

      args.removeFromDraftState(idsToRemove);
      args.onAfterPrune?.();
    },
    [compareMode, getBoundaryId, getFeaturesForDirtyCheck, isNewDraftId],
  );

  const handlePolygonEditStart = useCallback((identifier: PolygonIdentifier) => {
    const id = identifier.value;
    if (id != null && id !== "") {
      resetUserTouched(userTouchedRef.current, String(id));
    }
  }, []);

  const handleDrawUpdate = useCallback(
    (polygon: GeoJSON.Feature) => {
      const editingId = layerRef.current?.editingIdentifier?.value;
      const id =
        editingId != null && editingId !== ""
          ? String(editingId)
          : getBoundaryId(polygon as GeoJSON.Feature<GeoJSON.Polygon>);
      if (id) {
        markUserTouched(userTouchedRef.current, id);
      }
    },
    [getBoundaryId, layerRef],
  );

  const createPersistCurrentEditing = useCallback(
    (deps: CreatePersistCurrentEditingDeps) => {
      return (persistOptions?: PersistCurrentEditingOptions) => {
        const live = getLiveEditingSnapshot();

        if (live) {
          const existing = deps.getExistingFeature(live.boundaryId);
          const feature = deps.buildFeatureFromLive(live, existing);

          if (!feature) {
            deps.onMissingLayerId?.();
            layerRef.current?.stopEdit({ endDrawSession: false });
            deps.pruneAfterPersist(persistOptions);
            return;
          }

          const dirty = isDraftFeatureDirty(
            feature,
            draftBaselinesRef.current.get(live.boundaryId),
            isNewDraftId,
            compareMode,
            getBoundaryId,
            userTouchedRef.current,
          );

          if (dirty || persistOptions?.keepLiveInDraft) {
            deps.upsertToDraft(feature);
            if (persistOptions?.keepLiveInDraft) {
              pinBoundaryForSelection(live.boundaryId);
            }
          } else if (selectionPinnedDraftIdsRef.current.has(live.boundaryId)) {
            deps.upsertToDraft(feature);
          } else {
            deps.removeFromDraft(live.boundaryId);
            resetUserTouched(userTouchedRef.current, live.boundaryId);
            unpinBoundaryForSelection(live.boundaryId);
          }

          layerRef.current?.stopEdit({ endDrawSession: false });
        }

        const preserveBoundaryIds = [
          ...(persistOptions?.preserveBoundaryIds ?? []),
          ...(persistOptions?.keepLiveInDraft && live ? [live.boundaryId] : []),
        ];

        deps.pruneAfterPersist({
          ...persistOptions,
          preserveBoundaryIds: preserveBoundaryIds.length > 0 ? preserveBoundaryIds : undefined,
        });
      };
    },
    [
      compareMode,
      getBoundaryId,
      getLiveEditingSnapshot,
      isNewDraftId,
      layerRef,
      pinBoundaryForSelection,
      unpinBoundaryForSelection,
    ],
  );

  return {
    draftBaselinesRef,
    userTouchedRef,
    hasDraftChanges,
    getHasDraftChanges,
    getDirtyFeatures,
    getDirtyFeaturesIncludingLiveEditing,
    captureBaseline,
    replaceBaselineFromFeature,
    clearAllDirtyState,
    removeDirtyStateForId,
    markBoundaryUserTouched,
    syncCombineDirtyState,
    syncSelectionPinsToBoundaryIds,
    pruneOrphanDirtyState,
    pruneUnchangedDraftFeatures,
    handlePolygonEditStart,
    handleDrawUpdate,
    createPersistCurrentEditing,
  };
}
